import { Disposable, IDisposable } from "../../../base/common/lifecycle.js";
import { createDecorator, registerSingleton } from "../../instantiation/common/registry.js";
import { SyncDescriptor } from "../../instantiation/common/descriptors.js";
import { IContextService } from "../../context/common/contextService.js";
import { ILoggerService, ILogService } from "../../log/common/log.js";
import { Emitter, Event } from "../../../base/common/events.js";
import { ICommandService } from "../../commands/common/commands.js";
import { IKeyboardService } from "../../keyboard/common/keyboardService.js";
import { StandardKeyboardEvent } from "../../../base/browser/keyboardEvent.js";
import { IContextKeyService } from "../../contextkey/common/contextkey.js";
import { ResolvedKeybinding } from "../../../base/common/keybindings.js";
import { USLayoutResolvedKeybinding } from "./usLayoutResolvedKeybinding.js";
import { OperatingSystem, OS } from "../../../base/common/platform.js";

/**
 * Represents a keyboard key combination.
 * Examples: "Ctrl+Z", "Shift+Alt+F", "Delete", "Escape"
 */
export type KeyCombo = string;

/**
 * A keybinding maps a key combination to a command with an optional context condition.
 */
export interface IKeybinding {
	/**
	 * Unique identifier for this keybinding
	 */
	readonly id: string;

	/**
	 * The key combination that triggers this binding
	 * Examples: "Ctrl+Z", "Delete", "Shift+Alt+F"
	 */
	readonly key: KeyCombo | KeyCombo[];

	/**
	 * The command ID to execute when this keybinding is triggered
	 */
	readonly commandId: string;

	/**
	 * Optional context condition - when clause that must be true for the keybinding to be active
	 * Examples: "treeViewFocus", "!textInputFocus", "treeViewFocus && !modalOpen"
	 */
	readonly when?: string;

	/**
	 * Arguments to pass to the command when executed
	 */
	readonly args?: any;

	preventDefault?: boolean;
}

export interface IResolvedKeybinding {
	getLabel(): string;
}

/**
 * Service for managing keybindings and keyboard shortcuts.
 *
 * Listens to keyboard events and executes commands when keybindings are triggered.
 * Supports context-aware keybindings using "when" clauses.
 */
export interface IKeybindingService {
	/**
	 * Event fired when a keybinding is added or removed
	 */
	readonly onDidUpdateKeybindings: Event<void>;

	/**
	 * Register a keybinding
	 * @returns Disposable to unregister the keybinding
	 */
	registerKeybinding(binding: IKeybinding): IDisposable;

	/**
	 * Get all registered keybindings
	 */
	getKeybindings(): readonly IKeybinding[];

	/**
	 * Look up the keybinding for a command, optionally considering context
	 */
	lookupKeybinding(commandId: string, context?: IContextKeyService, enforceContextCheck?: boolean): IResolvedKeybinding | undefined;

	/**
	 * Get keybindings for a specific command
	 */
	getKeybindingsForCommand(commandId: string): readonly IKeybinding[];


	/**
	 * Resolve which keybinding (if any) would be triggered by the given keyboard event
	 */
	resolveKeybinding(event: KeyboardEvent): IKeybinding | undefined;
}



// #region Implementation

export const IKeybindingService = createDecorator<IKeybindingService>('keybindingService');

export class KeybindingService extends Disposable implements IKeybindingService {
	declare readonly _serviceBrand: undefined;

	private readonly _onDidUpdateKeybindings = this._register(new Emitter<void>());
	readonly onDidUpdateKeybindings = this._onDidUpdateKeybindings.event;

	private readonly keybindings = new Map<string, IKeybinding>();

	constructor(
		@ICommandService private readonly commandService: ICommandService,
		@IContextService private readonly contextService: IContextService,
		@IKeyboardService private readonly keyboardService: IKeyboardService,
		@ILogService private readonly logger: ILogService
	) {
		super();
		this.registerGlobalKeyboardListener();
	}

	lookupKeybinding(commandId: string, context?: IContextKeyService, enforceContextCheck?: boolean): IResolvedKeybinding | undefined {
		const candidates = Array.from(this.keybindings.values()).filter(kb => kb.commandId === commandId);

		if (candidates.length === 0) {
			return undefined;
		}

		// If context is provided, filter by "when" clause
		if (context) {
			for (const candidate of candidates) {
				if (!candidate.when) {
				}
			}
		}

		const first = candidates[0];
		const key = Array.isArray(first.key) ? first.key[0] : first.key;
		return {
			getLabel: () => key
		}
	}


	private registerGlobalKeyboardListener(): void {
		this._register(this.keyboardService.onKeyDown(this.handleKeyboardEvent));
	}

	private handleKeyboardEvent = (evt: StandardKeyboardEvent) => {
		const event = evt.browserEvent;
		const binding = this.resolveKeybinding(event);

		if (!binding) {
			return; // No matching keybinding
		}

		// Check if command can execute
		if (!this.commandService.canExecuteCommand(binding.commandId, binding.args)) {
			return;
		}

		if (binding.preventDefault === true) {
			evt.preventDefault();
		}

		// Execute the command
		this.commandService.executeCommand(binding.commandId, binding.args, { isAi: false, keyboardEvent: evt }).catch(err => {
			this.logger.error(`Failed to execute command '${binding.commandId}' from keybinding:`, err);
		});
	}

	registerKeybinding(binding: IKeybinding): IDisposable {
		if (this.keybindings.has(binding.id)) {
			this.logger.warn(`Keybinding with id '${binding.id}' already exists, overwriting`);
		}

		this.keybindings.set(binding.id, binding);
		this._onDidUpdateKeybindings.fire();

		return {
			dispose: () => {
				this.keybindings.delete(binding.id);
				this._onDidUpdateKeybindings.fire();
			}
		};
	}

	getKeybindings(): readonly IKeybinding[] {
		return Array.from(this.keybindings.values());
	}

	getKeybindingsForCommand(commandId: string): readonly IKeybinding[] {
		return Array.from(this.keybindings.values()).filter(kb => kb.commandId === commandId);
	}

	resolveKeybinding(event: KeyboardEvent): IKeybinding | undefined {
		// Build the key combo string from the event
		const keyCombo = this.buildKeyCombo(event);

		// Find matching keybindings
		const candidates = Array.from(this.keybindings.values()).filter(kb => {
			if (Array.isArray(kb.key)) {
				return kb.key.some(key => this.normalizeKeyCombo(key) === keyCombo);
			} else {
				return this.normalizeKeyCombo(kb.key) === keyCombo;
			}
		});

		if (candidates.length === 0) {
			return undefined;
		}

		// Filter by context - find the first keybinding whose "when" clause is satisfied
		for (const candidate of candidates) {
			if (!candidate.when || this.contextService.evaluate(candidate.when)) {
				return candidate;
			}
		}

		return undefined;
	}

	/**
	 * Build a normalized key combo string from a keyboard event.
	 * Examples: "ctrl+z", "shift+alt+f", "delete", "escape"
	 */
	private buildKeyCombo(event: KeyboardEvent): string {
		const parts: string[] = [];

		// Add modifiers in consistent order
		if (event.ctrlKey || event.metaKey) parts.push('ctrl'); // Treat Cmd as Ctrl
		if (event.altKey) parts.push('alt');
		if (event.shiftKey) parts.push('shift');

		// Add the main key
		const key = this.normalizeKey(event.key);
		if (key) {
			parts.push(key);
		}

		return parts.join('+');
	}

	/**
	 * Normalize a key string to lowercase and handle special cases
	 */
	private normalizeKey(key: string): string {
		// Handle special keys
		const normalized = key?.toLowerCase();

		// Map some keys to more consistent names
		const keyMap: Record<string, string> = {
			'control': 'ctrl',
			'command': 'ctrl',
			'meta': 'ctrl',
			'option': 'alt',
			' ': 'space',
		};

		return keyMap[normalized] || normalized;
	}

	/**
	 * Normalize a key combo string (e.g., "Ctrl+Z" -> "ctrl+z")
	 */
	private normalizeKeyCombo(keyCombo: string): string {
		const parts = keyCombo.toLowerCase().split('+').map(p => p.trim());

		// Sort modifiers consistently
		const modifiers: string[] = [];
		let mainKey = '';

		for (const part of parts) {
			const normalized = this.normalizeKey(part);
			if (normalized === 'ctrl' || normalized === 'alt' || normalized === 'shift') {
				if (!modifiers.includes(normalized)) {
					modifiers.push(normalized);
				}
			} else {
				mainKey = normalized;
			}
		}

		// Sort modifiers: ctrl, alt, shift
		modifiers.sort((a, b) => {
			const order = ['ctrl', 'alt', 'shift'];
			return order.indexOf(a) - order.indexOf(b);
		});

		if (mainKey) {
			modifiers.push(mainKey);
		}

		return modifiers.join('+');
	}
}

registerSingleton(IKeybindingService, new SyncDescriptor(KeybindingService));
